<template>
  <Teleport to="body">
    <Transition name="qo-fab">
      <button
        v-if="visible"
        type="button"
        class="qo-fab"
        :style="fabStyle"
        :aria-label="$t('public.quickOrder.yourOrder') + ' (' + itemCount + ')'"
        @click="$emit('open')"
      >
        <span class="material-icons qo-fab__icon" aria-hidden="true">shopping_cart</span>
        <span class="qo-fab__badge" aria-hidden="true">{{ itemCount }}</span>
      </button>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  itemCount: { type: Number, default: 0 },
  primaryColor: { type: String, default: '' },
})

defineEmits(['open'])

const visible = computed(() => props.itemCount > 0)

const fabStyle = computed(() => {
  const bg = props.primaryColor || '#e85a2b'
  return {
    backgroundColor: bg,
    '--qo-fab-badge-bg': darkenColor(bg, 0.35),
  }
})

function darkenColor(hex, amount) {
  const h = hex.replace('#', '')
  const num = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16)
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)))
  const g = Math.max(0, Math.round(((num >> 8) & 0xff) * (1 - amount)))
  const b = Math.max(0, Math.round((num & 0xff) * (1 - amount)))
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`
}
</script>

<style scoped>
.qo-fab {
  position: fixed;
  bottom: 7rem;
  right: 1rem;
  z-index: 999;
  min-width: 56px;
  min-height: 56px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s, opacity 0.15s;
}

.qo-fab:hover {
  transform: scale(1.05);
}

.qo-fab:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.qo-fab__icon {
  font-size: 1.5rem;
}

.qo-fab__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 22px;
  height: 22px;
  padding: 0 5px;
  border-radius: 11px;
  background: var(--qo-fab-badge-bg, #1e3a5f);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
  pointer-events: none;
}

@media (min-width: 768px) {
  .qo-fab {
    bottom: 2rem;
    right: 2rem;
  }
}

.qo-fab-enter-active,
.qo-fab-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.qo-fab-enter-from,
.qo-fab-leave-to {
  transform: scale(0.5);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .qo-fab-enter-active,
  .qo-fab-leave-active {
    transition: none;
  }
}
</style>
